/* Product-specific mug colour preview. The native Shopify picker remains the only variant authority. */
(() => {
  if (customElements.get('fiorpaw-mug-colour-preview')) return;

  class FiorpawMugColourPreview extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      try {
        this.config = JSON.parse(this.querySelector('[data-fp-mug-config]').textContent);
      } catch (error) {
        console.warn('Fiorpaw colour preview: invalid configuration.', error);
        return;
      }
      this.controller = new AbortController();
      this.options = { signal: this.controller.signal };
      this.section = this.closest('.shopify-section') || document;
      this.image = this.querySelector('[data-fp-mug-image]');
      this.imageStatus = this.querySelector('[data-fp-mug-image-status]');
      this.announcement = this.querySelector('[data-fp-mug-announcement]');
      this.dialog = this.querySelector('[data-fp-mug-dialog]');
      this.dialogImage = this.querySelector('[data-fp-mug-dialog-image]');
      this.requestVersion = 0;
      this.loadedUrls = new Set();
      this.selectedId = '';
      this.viewButtons = [...this.querySelectorAll('[data-fp-mug-view]')];
      this.viewButtons.forEach(button => {
        button.addEventListener('click', () => this.showView(button.dataset.fpMugView, true), this.options);
        button.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const current = this.viewButtons.indexOf(button);
          const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.viewButtons.length - 1
            : (current + (event.key === 'ArrowRight' ? 1 : -1) + this.viewButtons.length) % this.viewButtons.length;
          this.viewButtons[index].focus();
          this.showView(this.viewButtons[index].dataset.fpMugView, true);
        }, this.options);
      });
      this.querySelectorAll('[data-fp-mug-asset-zoom]').forEach(button => {
        button.addEventListener('click', () => this.openAsset(button), this.options);
      });
      this.installCompactPreview();

      // This observes the existing radio; it never sets form values, IDs or purchase state.
      document.addEventListener('change', event => {
        const picker = event.target.closest?.('variant-picker');
        if (!this.ownsPicker(picker)) return;
        const selected = event.target instanceof HTMLSelectElement
          ? event.target.options[event.target.selectedIndex]
          : event.target;
        const id = selected.dataset.variantId;
        if (id && this.config.variants[id]) this.showVariant(id, true);
      }, { ...this.options, capture: true });

      document.addEventListener('variant:update', event => {
        if (!this.ownsPicker(event.target.closest?.('variant-picker'))) return;
        if (String(event.detail?.data?.productId) !== this.config.productId) return;
        this.installCompactPreview();
        const id = String(event.detail?.resource?.id || '');
        if (this.config.variants[id]) this.showVariant(id, true);
      }, this.options);

      this.querySelector('[data-fp-mug-enlarge]').addEventListener('click', event => this.openPreview(event.currentTarget), this.options);
      this.querySelector('[data-fp-mug-close]').addEventListener('click', () => this.dialog.close(), this.options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, this.options);
      this.dialog.addEventListener('close', () => {
        if (this.dialogTrigger?.isConnected) this.dialogTrigger.focus({ preventScroll: true });
      }, this.options);

      const picker = this.findPicker();
      const selectedInput = picker?.querySelector('input:checked[data-variant-id]');
      this.showVariant(selectedInput?.dataset.variantId || this.config.initialVariantId, false);
      this.idlePreload = 'requestIdleCallback' in window
        ? window.requestIdleCallback(() => this.preloadRemaining(), { timeout: 1800 })
        : window.setTimeout(() => this.preloadRemaining(), 1000);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.requestVersion += 1;
      if ('cancelIdleCallback' in window) window.cancelIdleCallback(this.idlePreload);
      else window.clearTimeout(this.idlePreload);
      this.mini?.remove();
      if (this.dialog?.open) this.dialog.close();
    }

    findPicker() {
      return this.section.querySelector(`variant-picker[data-product-id="${this.config.productId}"]`);
    }

    ownsPicker(picker) {
      return picker?.dataset.productId === this.config.productId && this.section.contains(picker);
    }

    installCompactPreview() {
      const picker = this.findPicker();
      if (!picker) return;
      if (this.mini?.isConnected) return;
      const existing = this.section.querySelector(`[data-fp-mug-compact="${this.config.productId}"]`);
      if (existing) existing.remove();
      this.mini = document.createElement('button');
      this.mini.type = 'button';
      this.mini.className = 'fp-mug-preview__compact';
      this.mini.dataset.fpMugCompact = this.config.productId;
      this.mini.setAttribute('aria-label', this.config.text.zoom);
      this.miniImage = document.createElement('img');
      this.miniImage.width = 84;
      this.miniImage.height = 84;
      this.miniImage.alt = '';
      const copy = document.createElement('span');
      copy.className = 'fp-mug-preview__compact-copy';
      const title = document.createElement('span');
      title.className = 'fp-mug-preview__compact-title';
      title.textContent = this.config.text.parts;
      this.miniLabel = document.createElement('strong');
      this.miniHint = document.createElement('span');
      this.miniHint.className = 'fp-mug-preview__compact-hint';
      this.miniHint.textContent = this.config.text.mobileHint;
      copy.append(title, this.miniLabel, this.miniHint);
      this.mini.append(this.miniImage, copy);
      this.mini.addEventListener('click', () => this.openPreview(this.mini), this.options);
      picker.after(this.mini);
    }

    showVariant(id, announce) {
      const entry = this.config.variants[String(id)];
      if (!entry) return;
      if (announce && this.selectedId !== String(id)) this.showView('colour', false);
      if (this.selectedId === String(id) && this.dataset.previewState === 'ready') {
        this.updateLabels(entry);
        if (this.miniImage) this.miniImage.src = entry.url;
        this.setState('ready');
        return;
      }
      const version = ++this.requestVersion;
      this.selectedId = String(id);
      this.updateLabels(entry);
      this.setState('loading');
      const loader = new Image();
      loader.onload = async () => {
        try { await loader.decode?.(); } catch (_) { /* onload still proves a usable image. */ }
        if (!this.isConnected || version !== this.requestVersion) return;
        this.loadedUrls.add(entry.url);
        this.image.src = entry.url;
        this.image.alt = `${this.config.text.heading}: ${entry.label} — ${this.config.text.parts}`;
        this.querySelectorAll('[data-fp-mug-story-image], [data-fp-mug-colour-thumb], [data-fp-mug-story-thumb]').forEach(image => { image.src = entry.url; });
        if (!this.dialog.open || this.dialog.dataset.mode === 'colour') {
          this.dialogImage.src = entry.url;
          this.dialogImage.alt = this.image.alt;
        }
        if (this.miniImage) this.miniImage.src = entry.url;
        this.setState('ready');
        if (announce) this.announcement.textContent = `${this.config.text.parts}: ${entry.label}.`;
      };
      loader.onerror = () => {
        if (!this.isConnected || version !== this.requestVersion) return;
        this.setState('error');
        this.announcement.textContent = this.config.text.error;
      };
      if (!entry.url || /__MUG_[A-Z]+_URL__/.test(entry.url)) {
        loader.onerror();
        return;
      }
      loader.src = entry.url;
    }

    updateLabels(entry) {
      this.querySelector('[data-fp-mug-selected-label]').textContent = entry.label;
      if (!this.dialog.open || this.dialog.dataset.mode === 'colour') this.querySelector('[data-fp-mug-dialog-label]').textContent = `${this.config.text.heading} — ${entry.label}`;
      if (this.miniLabel) this.miniLabel.textContent = entry.label;
      if (this.mini) this.mini.setAttribute('aria-label', `${this.config.text.zoom}: ${entry.label}`);
    }

    setState(state) {
      this.dataset.previewState = state;
      this.setAttribute('aria-busy', String(state === 'loading'));
      this.imageStatus.hidden = state === 'ready';
      this.imageStatus.textContent = state === 'error' ? this.config.text.error : this.config.text.loading;
      const unavailable = state !== 'ready';
      this.querySelector('[data-fp-mug-enlarge]').disabled = unavailable;
      if (this.mini) {
        this.mini.dataset.previewState = state;
        this.mini.disabled = unavailable;
        this.miniHint.textContent = state === 'ready' ? this.config.text.mobileHint
          : state === 'error' ? this.config.text.error : this.config.text.loading;
      }
    }

    openPreview(trigger) {
      if (this.dataset.previewState !== 'ready' || this.dialog.open) return;
      this.dialogTrigger = trigger;
      this.dialog.dataset.mode = 'colour';
      this.dialogImage.src = this.image.src;
      this.dialogImage.alt = this.image.alt;
      this.querySelector('[data-fp-mug-dialog-label]').textContent = `${this.config.text.heading} — ${this.config.variants[this.selectedId].label}`;
      this.querySelector('[data-fp-mug-dialog-note]').textContent = this.config.text.note;
      this.dialog.showModal();
    }

    showView(view, announce) {
      const panel = this.querySelector(`[data-fp-mug-panel="${view}"]`);
      if (!panel) return;
      this.dataset.activeView = view;
      this.querySelectorAll('[data-fp-mug-panel]').forEach(item => { item.hidden = item !== panel; });
      this.viewButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.fpMugView === view)));
      this.querySelector('[data-fp-mug-colour-caption]').hidden = view !== 'colour';
      this.querySelector('[data-fp-mug-static-caption]').hidden = !(['lifestyle', 'detail'].includes(view) || view.startsWith('photo-'));
      const staticImage = panel.querySelector('.fp-mug-preview__static-image');
      if (staticImage) this.ensurePhotoLoaded(panel, staticImage);
      if (announce) this.announcement.textContent = this.viewButtons.find(button => button.dataset.fpMugView === view)?.textContent.trim() || '';
    }

    ensurePhotoLoaded(panel, image) {
      let status = panel.querySelector('[data-fp-mug-photo-status]');
      if (!status) {
        status = document.createElement('p');
        status.className = 'fp-mug-preview__image-status';
        status.dataset.fpMugPhotoStatus = '';
        status.setAttribute('role', 'status');
        panel.append(status);
        const finish = () => {
          const ok = image.complete && image.naturalWidth > 0;
          status.hidden = ok;
          status.textContent = ok ? '' : this.config.text.photoError;
          panel.setAttribute('aria-busy', 'false');
          panel.querySelector('button').disabled = !ok;
        };
        image.addEventListener('load', finish, this.options);
        image.addEventListener('error', finish, this.options);
      }
      const ok = image.complete && image.naturalWidth > 0;
      status.hidden = ok;
      status.textContent = ok ? '' : image.complete ? this.config.text.photoError : this.config.text.photoLoading;
      panel.setAttribute('aria-busy', String(!image.complete));
      panel.querySelector('button').disabled = !ok;
    }

    openAsset(trigger) {
      if (this.dialog.open) return;
      const image = trigger.querySelector('img');
      if (!image?.complete || !image.naturalWidth) return;
      this.dialogTrigger = trigger;
      this.dialog.dataset.mode = 'asset';
      this.dialogImage.src = image.src;
      this.dialogImage.alt = image.alt;
      this.querySelector('[data-fp-mug-dialog-label]').textContent = trigger.dataset.label;
      this.querySelector('[data-fp-mug-dialog-note]').textContent = this.querySelector('[data-fp-mug-static-caption]').textContent;
      this.dialog.showModal();
    }

    preloadRemaining() {
      if (!this.isConnected || navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
      Object.values(this.config.variants).forEach(entry => {
        if (!entry.url || this.loadedUrls.has(entry.url) || /__MUG_[A-Z]+_URL__/.test(entry.url)) return;
        const image = new Image();
        image.onload = () => this.loadedUrls.add(entry.url);
        image.src = entry.url;
      });
    }
  }
  customElements.define('fiorpaw-mug-colour-preview', FiorpawMugColourPreview);
})();
